import { useEffect, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import {
  ExperienceContext,
  experienceEntryPath,
  organizationExperienceHome,
  type ExperienceDefinition,
} from "./experience";
import { listedProducts } from "./product-access";
import { SupportModeBanner } from "./SupportModeBanner";
import type { OrganizationContext, ShellContext } from "./types";

function isModifiedClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return event.ctrlKey || event.metaKey || event.shiftKey || event.altKey;
}

export function ProductShell({ experience, organization, context, navigate, staffNavigation, children }: {
  experience: ExperienceDefinition;
  organization: OrganizationContext;
  context: ShellContext;
  navigate: (path: string) => void;
  staffNavigation: boolean;
  children: ReactNode;
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreButton = useRef<HTMLButtonElement>(null);
  const morePanel = useRef<HTMLElement>(null);
  const pathname = window.location.pathname;
  const base = `/o/${organization.organization.slug}/${experience.routeBase}`;
  const productHome = experienceEntryPath(organization, experience);
  const platformHome = `/o/${organization.organization.slug}/home`;
  const organizationSettings = `/o/${organization.organization.slug}/settings`;
  const products = listedProducts(organization.products).filter(({ access }) => access.allowed);
  const mobilePrimary = experience.primaryNavigation.slice(0, 4);
  const mobileOverflow = experience.primaryNavigation.slice(4);

  useEffect(() => { setMoreOpen(false); }, [pathname]);
  useEffect(() => { if (moreOpen) morePanel.current?.focus(); }, [moreOpen]);

  const closeMore = () => {
    setMoreOpen(false);
    moreButton.current?.focus();
  };

  const link = (path: string, label: string, icon?: string, home = false) => {
    const current = pathname === path
      || pathname.startsWith(`${path}/`)
      || (home && [base, `${base}/`].includes(pathname));

    return <a
      key={path}
      href={path}
      aria-current={current ? "page" : undefined}
      onClick={(event) => {
        if (isModifiedClick(event)) return;
        event.preventDefault();
        setMoreOpen(false);
        navigate(path);
      }}
    >
      {icon && <span
        className="experience-icon"
        aria-hidden="true"
        style={{ "--experience-icon": `var(--cd-icon-${icon})` } as CSSProperties}
      />}
      {label}
    </a>;
  };

  const navigationLink = (item: ExperienceDefinition["primaryNavigation"][number]) => (
    link(`${base}/${item.path}`, item.label, item.icon, item.path === experience.homePath)
  );

  const secondary = <>
    {link(organizationSettings, "Organización en ClickDialog")}
    {link("/account", "Mi cuenta y seguridad")}
    {products.length > 1 && <div className="experience-other-products">
      <p>Otros productos</p>
      {products
        .filter(({ manifest }) => manifest.routeBase !== experience.routeBase)
        .map(({ manifest }) => link(`/o/${organization.organization.slug}/${manifest.routeBase}`, manifest.displayName))}
    </div>}
    {staffNavigation && <div className="experience-staff">
      <p>Equipo ClickDialog</p>
      {link("/crm", "Operación interna")}
      {link("/demos", "Demos")}
    </div>}
  </>;

  return <ExperienceContext.Provider value={experience}>
    <div className="experience-shell" data-experience={experience.key} data-experience-mode={experience.mode}>
      <a className="skip-link" href="#main-content">Saltar al contenido</a>

      <aside className="experience-sidebar">
        <a
          className="experience-brand"
          href={platformHome}
          onClick={(event) => {
            if (isModifiedClick(event)) return;
            event.preventDefault();
            navigate(platformHome);
          }}
          aria-label="Volver a ClickDialog Platform"
        >
          ClickDialog
        </a>
        <a
          className="experience-name"
          href={productHome}
          onClick={(event) => {
            if (isModifiedClick(event)) return;
            event.preventDefault();
            navigate(productHome);
          }}
        >
          {experience.displayName}
        </a>

        <nav aria-label={`Navegación principal de ${experience.displayName}`} className="experience-primary">
          {experience.primaryNavigation.map(navigationLink)}
        </nav>

        <div className="experience-sidebar-footer">
          <a
            className="experience-platform-return"
            href={platformHome}
            onClick={(event) => {
              if (isModifiedClick(event)) return;
              event.preventDefault();
              navigate(platformHome);
            }}
          >
            ← Plataforma
          </a>
          <details className="experience-secondary">
            <summary>Cuenta y organización</summary>
            {secondary}
          </details>
        </div>
      </aside>

      <div className="experience-main">
        <SupportModeBanner context={context} organization={organization} />
        <header className="experience-topbar">
          <select
            aria-label="Cambiar negocio"
            value={organization.organization.slug}
            onChange={(event) => {
              const target = context.organizations.find((item) => item.organization.slug === event.target.value);
              if (target) navigate(organizationExperienceHome(target));
            }}
          >
            {context.organizations
              .filter((item) => item.organization.status === "active")
              .map(({ organization: item }) => <option key={item.id} value={item.slug}>{item.displayName}</option>)}
          </select>
          <button
            className="experience-account"
            aria-label={`Mi cuenta: ${context.user.displayName}`}
            onClick={() => navigate("/account")}
          >
            <span aria-hidden="true">{context.user.displayName.slice(0, 1)}</span>
            <span className="experience-account-name">{context.user.displayName}</span>
          </button>
        </header>

        <main id="main-content" className="experience-content" tabIndex={-1}>
          <div className={[
            "experience-product-surface",
            `experience-product-surface--${experience.key}`,
            experience.surfaceClassName,
          ].filter(Boolean).join(" ")}>
            {children}
          </div>
        </main>

        {moreOpen && <section
          ref={morePanel}
          tabIndex={-1}
          className="experience-more"
          id="experience-more"
          aria-label="Más opciones"
          onKeyDown={(event) => { if (event.key === "Escape") closeMore(); }}
        >
          <div>
            <h2>Más opciones</h2>
            <button onClick={closeMore} aria-label="Cerrar más opciones">Cerrar</button>
          </div>
          {mobileOverflow.map(navigationLink)}
          {secondary}
        </section>}

        <nav className="experience-mobile-nav" aria-label={`Navegación móvil de ${experience.displayName}`}>
          {mobilePrimary.map((item) => link(
            `${base}/${item.path}`,
            item.mobileLabel,
            item.icon,
            item.path === experience.homePath,
          ))}
          <button
            ref={moreButton}
            aria-expanded={moreOpen}
            aria-controls="experience-more"
            onClick={() => setMoreOpen(!moreOpen)}
          >
            <span
              className="experience-icon"
              aria-hidden="true"
              style={{ "--experience-icon": "var(--cd-icon-settings)" } as CSSProperties}
            />
            Más
          </button>
        </nav>
      </div>
    </div>
  </ExperienceContext.Provider>;
}